<template>
  <div class="position-relative">
    <LMap class="location-map" :zoom="map.zoom" :center="map.center" @update:center="centerUpdate"
      @update:bounds="boundsUpdate" ref="leaflet" @ready="addBasemapLayer(BASEMAP_ACTIVE)" :options="map.options">
      <LFeatureGroup ref="featureGroup" id="featureGroup"></LFeatureGroup>

      <LocationMarkers :locations="visibleLocations" :mapRef="mapObject" />

      <LLayerGroup>
        <LMarker :lat-lng="home" v-if="home && home.lat && home.lng">
          <MarkerPin><i class="fa fa-home"></i></MarkerPin>
          <LTooltip content="Project Home" />
        </LMarker>
        <LRectangle v-if="searchArea.area && searchArea.area.bounds" :bounds="searchArea.area.bounds"
          :l-style="searchArea.style">
        </LRectangle>
        <LCircle v-else-if="
          searchArea.area &&
          searchArea.area.lngLat &&
          searchArea.area.accuracy
        " :lat-lng="searchArea.area.lngLat" :radius="searchArea.area.accuracy" :l-style="searchArea.style">
        </LCircle>
      </LLayerGroup>

      <LControlZoom position="bottomright"></LControlZoom>
    </LMap>
    <div class="map-layers bg-white border rounded">
      <div class="row no-gutters d-inline">
        <BaseMapPicker class="col-6 col-md-12 p-1" />
        <b-form-group class="col-6 col-md-12 p-1 mb-0" v-if="layers.length > 0">
          <label for="map-layers" class="font-weight-bold">Additional Layers</label>
          <b-form-checkbox-group id="map-layers" v-model="layerChecked" :options="layers" stacked>
          </b-form-checkbox-group>
        </b-form-group>
        <div class="col-12 border-top">
          <DiscreteDataLegend :makeSmall="true"></DiscreteDataLegend>
        </div>
      </div>
    </div>
    <div class="position-absolute map-upper-right">
      <GpsGeocoderInput v-on:input="setMapView" ref="GpsGeocoderInput" />
      <LocationPrivacyTooltip :project="project" :isManager="isManager" />
    </div>
    <div class="position-absolute map-loading" v-if="locationsIsLoading">
      <i class="fa fa-spin fa-spinner ml-3"></i>
      Loading Locations {{ fetchedPercent }}%
    </div>
    <div class="position-absolute map-loading p-1 text-white" :class="`bg-${messageType}`" v-else-if="message">
      {{ message }}
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";

import * as L from "leaflet";
import * as EsriLeaflet from "esri-leaflet";

import {
  LMap,
  LTileLayer,
  LFeatureGroup,
  LLayerGroup,
  LMarker,
  LPopup,
  LTooltip,
  LRectangle,
  LCircle,
  LControlZoom,
} from "vue2-leaflet";

import MarkerCrosshairs from "../../../common/map/MarkerCrosshairs.vue";
import MarkerPin from "../../../common/map/MarkerPin.vue";
import GpsGeocoderInput from "../../../common/map/gpsGeocoderInput.vue";
import BaseMapPicker from "../../../common/map/BaseMapPicker.vue";
import LocationMarkers from "../map/LocationMarkers.vue";
import { viridis } from "../../../../utils/viridisColorScale";
import { getContrastColorFromRgba } from "../../../../utils/contrastColor";
import DiscreteDataLegend from "../map/DiscreteDataLegend.vue";
import LocationPrivacyTooltip from "../../../common/map/LocationPrivacyTooltip.vue";

export default {
  name: "projectLocationsMap",
  components: {
    LMap,
    LTileLayer,
    LFeatureGroup,
    LLayerGroup,
    LMarker,
    LPopup,
    LTooltip,
    LRectangle,
    LCircle,
    LControlZoom,
    MarkerCrosshairs,
    MarkerPin,
    GpsGeocoderInput,
    BaseMapPicker,
    LocationMarkers,
    DiscreteDataLegend,
    LocationPrivacyTooltip,
  },

  data() {
    return {
      map: {
        zoom: 8,
        center: { lng: -90, lat: 40 },
        options: {
          zoomControl: false,
        },
        // Used to find the closest assignments before panning finishes, while still allowing panning
        closestCenter: { lng: -90, lat: 40 },
        currentBounds: null,
        url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
        attribution:
          '&copy; <a target="_blank" rel="noopener noreferrer" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      },
      searchArea: {
        area: null,
        style: {
          color: "#000000",
          weight: 10,
          opacity: 0.8,
          fillOpacity: 0,
        },
      },
      layerChecked: [],
      addedLayers: [],
    };
  },
  mounted() {
    this.getMapTypes();
    if (this.home && this.home.lat && this.home.lng) {
      this.map.center = this.home;
      this.map.closestCenter = this.home;
    }
    this.fetchAllProjectLocations();
  },
  computed: {
    ...mapGetters({
      fetchedPercent: "project/map/fetchedPercent",
      project: "project/get/project",
      projectIdGet: "project/get/id",
      allLocations: "project/map/locations",
      resourceMaps: "project/resources/resourceMaps",
      BASEMAP_ACTIVE: "map/BASEMAP_ACTIVE",
      isManager: "project/get/isManager",
    }),
    ...mapState({
      locationsIsLoading: (state) => state.project.map.locationsIsLoading,
      message: (state) => state.project.map.message,
      messageType: (state) => state.project.map.messageType,
      projectIdMap: (state) => state.project.map.projectId,
    }),
    home() {
      if (this.project && this.project.latitude && this.project.longitude) {
        return {
          lat: this.project.latitude,
          lng: this.project.longitude,
        };
      }
      return null;
    },
    /**
     * Get only visible locations within current map bounds
     * This improves performance by only rendering markers in view
     * @returns {Array} - Locations within current viewport
     */
    visibleLocations() {
      if (!this.allLocations || !Array.isArray(this.allLocations)) return [];
      if (!this.map.currentBounds) return this.allLocations;

      try {
        return this.allLocations.filter((loc) => {
          if (!loc.lngLat || !loc.lngLat.lat || !loc.lngLat.lng) return false;
          return this.map.currentBounds.contains([
            loc.lngLat.lat,
            loc.lngLat.lng,
          ]);
        });
      } catch (e) {
        console.warn("Error calculating visibleLocations:", e);
        return this.allLocations;
      }
    },
    layers() {
      if (this.resourceMaps && this.resourceMaps.length > 0) {
        return this.resourceMaps.map((layer) => {
          return { text: layer.name, value: layer.id };
        });
      } else {
        return [];
      }
    },
    mapObject() {
      return this.$refs.leaflet && this.$refs.leaflet.mapObject
        ? this.$refs.leaflet.mapObject
        : null;
    },
    locationPrivacyText() {
      if (!this.project || this.project.locationPrivacy === undefined || this.project.locationPrivacy === null) {
        return ""
      }
      //return this.newProject.locationPrivacy
      const earthSizeSQKM = 510072000.0; // Earth's surface area in square kilometers
      const privacyLevel = this.project.locationPrivacy;
      const privacyAreaSQKM = earthSizeSQKM / (Math.pow(privacyLevel, 2) * 12);
      // round down to nearest 1 decimal place
      const roundedPrivacyAreaSQKM = Math.floor(privacyAreaSQKM * 10) / 10;
      return `${roundedPrivacyAreaSQKM.toLocaleString()}`;
    }
  },
  methods: {
    // Component methods
    ...mapActions({
      fetchAllProjectLocations: "project/map/fetchAllProjectLocations",
      getMapTypes: "project/resources/getMapTypes",
    }),
    centerUpdate(center) {
      this.map.center = center;
      this.map.closestCenter = center;
    },
    boundsUpdate(bounds) {
      // Store bounds coordinates to avoid circular reference issues
      if (bounds && bounds._southWest && bounds._northEast) {
        this.map.currentBounds = {
          _southWest: {
            lat: bounds._southWest.lat,
            lng: bounds._southWest.lng,
          },
          _northEast: {
            lat: bounds._northEast.lat,
            lng: bounds._northEast.lng,
          },
          contains: function (latlng) {
            const lat = Array.isArray(latlng) ? latlng[0] : latlng.lat;
            const lng = Array.isArray(latlng) ? latlng[1] : latlng.lng;
            return (
              lat >= this._southWest.lat &&
              lat <= this._northEast.lat &&
              lng >= this._southWest.lng &&
              lng <= this._northEast.lng
            );
          },
        };
      }
    },
    setMapView(searchArea) {
      console.log("setMapView", searchArea);
      this.searchArea.area = searchArea;
      if (searchArea && searchArea.bounds) {
        this.$refs.leaflet.mapObject.flyToBounds(searchArea.bounds);
      } else if (searchArea && searchArea.lngLat) {
        this.$refs.leaflet.mapObject.flyTo(searchArea.lngLat, searchArea.zoom);
      }
    },
    panToAll() {
      if (!(this.$refs.leaflet && this.$refs.leaflet.mapObject)) {
        // if map is not ready yet, try again shortly
        setTimeout(() => {
          this.panToAll();
        }, 500);
        return;
      }
      this.$nextTick(() => {
        let bounds = this.allLocations.map((location) => [
          location.lngLat.lat,
          location.lngLat.lng,
        ]);
        if (this.home && this.home.lat && this.home.lng) {
          bounds.push([this.home.lat, this.home.lng]);
        }
        if (bounds.length >= 2) {
          console.log("flying to bounds", bounds);
          this.$refs.leaflet.mapObject.flyToBounds(bounds, {
            paddingTopLeft: [40, 5],
            paddingBottomRight: [5, 5],
          });
        }
      });
    },
    addMapLayer(resourceUrl) {
      let featureLayer = EsriLeaflet.featureLayer({
        url: resourceUrl,
        onEachFeature: (feature, layer) => {
          // Add any additional properties or handling here
        },
      });
      this.addedLayers.push({ url: resourceUrl, layer: featureLayer });
      featureLayer.addTo(this.$refs.featureGroup.mapObject);
    },
    removeMapLayer(resourceUrl) {
      let featureLayer = this.addedLayers.filter((l) => {
        return l.url === resourceUrl;
      })[0];
      featureLayer.layer.removeFrom(this.$refs.featureGroup.mapObject);
      this.addedLayers.splice(this.addedLayers.indexOf(featureLayer), 1);
    },
    addBasemapLayer(newLayer, oldLayer) {
      const map = this.$refs.leaflet && this.$refs.leaflet.mapObject;
      if (!map || !newLayer) return;
      if (oldLayer) {
        try {
          map.removeLayer(oldLayer);
        } catch (e) { }
      }
      newLayer.addTo(map);
      map.invalidateSize();
    },
  },
  watch: {
    BASEMAP_ACTIVE(newLayer, oldLayer) {
      this.addBasemapLayer(newLayer, oldLayer);
    },
    fetchedPercent: {
      handler(fetchedPercent) {
        if (fetchedPercent < 99) {
          return;
        }
        setTimeout(() => {
          this.panToAll();
        }, 500);
      },
    },
    layerChecked(newVal, oldVal) {
      console.log("layerChecked changed", newVal, oldVal);
      let layersToAdd = newVal.filter((l) => !oldVal.includes(l));
      layersToAdd.forEach((val) => {
        this.resourceMaps.filter((res) => {
          if (val === res.id) {
            this.addMapLayer(res.url);
          }
        });
      });
      let layersToRemove = oldVal.filter((l) => !newVal.includes(l));
      layersToRemove.forEach((val) => {
        this.resourceMaps.filter((res) => {
          if (val === res.id) {
            this.removeMapLayer(res.url);
          }
        });
      });
    },
  },
};
</script>
<style scoped>
.location-map {
  width: 100%;
  height: 40vh;
  min-height: 400px;
}

.map-help {
  z-index: 999;
  top: 25%;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 10px;
  border-radius: 10px;
}

.map-upper-right {
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 999;

  @media (min-width: 768px) {
    width: 33%;
  }
}

.map-layers {
  @media (min-width: 768px) {
    position: absolute;
    bottom: 15px;
    left: 10px;
    width: 200px;
    z-index: 999;
  }
}

.map-loading {
  top: 10px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 999;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 2px;
  border-radius: 10px;
}
</style>
